// Copyright (c) 2012-2021 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved. NOTICE: All information contained herein
// is, and remains the property of GRAB. The intellectual and technical concepts contained herein are confidential, proprietary
// and controlled by GRAB and may be covered by patents, patents in process, and are protected by trade secret or copyright law.
//
// You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate, adapt or change this material
// in any way unless prior written permission is obtained from GRAB. Access to the source code contained herein is hereby
// forbidden to anyone except current GRAB employees or contractors with binding Confidentiality and Non-disclosure agreements
// explicitly covering such access.
//
// The copyright notice above does not evidence any actual or intended publication or disclosure of this source code,
// which includes information that is confidential and/or proprietary, and is a trade secret, of GRAB.
//
// ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR THROUGH USE OF THIS SOURCE
// CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE LAWS AND
// INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY
// OR IMPLY ANY RIGHTS TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING
// THAT IT MAY DESCRIBE, IN WHOLE OR IN PART.

import { Upload, message, Row, Col, Button, Progress } from 'antd'
import React, { useState, useEffect } from 'react'
import { InboxOutlined } from '@ant-design/icons'
import { csvToJSON, downloadCSV, jsonToCSV } from '../../utils/csvParser'
import { addDevices, IAddDevicesRes } from '../../api/devices'

const UploadArea = Upload.Dragger

/*
 * batch upload params:
 * "cityID"
 * "deviceID"
 * "driverID"
 * "taskType"
 * */

const KeyCityID = 'cityID'
const KeyDeviceID = 'deviceID'
const KeyDriverID = 'driverID'
const KeyTaskType = 'taskType'
const KeyDeviceType = 'deviceType'

type RecordItem = {
  [KeyCityID]: number
  [KeyDeviceID]: string
  [KeyTaskType]: number
  [KeyDriverID]: string
  [KeyDeviceType]: number
}
type ProcessedRecordItem = RecordItem & { comment?: string }
type SubmitResult = {
  total?: number
  fileName?: string
  accepted?: ProcessedRecordItem[]
  rejected?: ProcessedRecordItem[]
}
type RecordItemKey = keyof RecordItem
const keys: RecordItemKey[] = [KeyCityID, KeyDeviceID, KeyDriverID, KeyTaskType, KeyDeviceType]

const checkDataContent = async csvRecords => {
  csvRecords = csvRecords.filter(x => x.length > 1)
  const headerColumns = csvRecords[0].map(name => name.toLowerCase())
  const columnMapping = {}
  const columns = keys.map(name => name.toLowerCase())
  columns.forEach((column, columnIndex) => {
    const csvColumnIndex = headerColumns.indexOf(column)
    if (csvColumnIndex === -1) {
      throw new Error(column + ' not exist in columns')
    }
    columnMapping[keys[columnIndex]] = csvColumnIndex
  })
  const deviceIDs = {}
  const driverIDs = {}
  return csvRecords.slice(1, csvRecords.length).map((data, index) => {
    let record = {} as RecordItem
    keys.forEach(key => {
      const csvValue = data[columnMapping[key]] || ('' as string)
      if (key === KeyCityID || key === KeyTaskType || key === KeyDeviceType) {
        record[key] = (parseInt(csvValue.toString().trim()) as number) || 0
      } else {
        record[key] = csvValue.toString().trim()
      }
    })
    const deviceId = record[KeyDeviceID]
    const driverId = record[KeyDriverID]
    const cityId = record[KeyCityID]
    const taskType = record[KeyTaskType]
    const deviceType = record[KeyDeviceType]
    const csvRowIndex = index + 2
    if (!deviceId) {
      throw new Error('row ' + csvRowIndex + ' deviceID is missing')
    }
    if (!cityId) {
      throw new Error('row ' + csvRowIndex + ' cityID is invalid')
    }
    if (taskType !== 0 && taskType !== 1) {
      throw new Error('row ' + csvRowIndex + ' taskType is invalid')
    }
    if (deviceType !== 0 && deviceType !== 1) {
      throw new Error('row ' + csvRowIndex + ' deviceType is invalid')
    }
    if (deviceIDs[deviceId]) {
      throw new Error('deviceID ' + deviceId + ' repeat')
    }
    if (driverId && driverIDs[driverId]) {
      throw new Error('driverID ' + driverId + ' repeat')
    }
    deviceIDs[deviceId] = true
    if (driverId) {
      driverIDs[driverId] = true
    }
    record[KeyDriverID] = record[KeyDriverID] || null
    return record
  })
}

const parseAddDeviceResult = (res: IAddDevicesRes, devices: RecordItem[]): SubmitResult => {
  let result = {} as SubmitResult
  const requestCount = devices.length
  result.accepted = []
  result.rejected = []
  result.total = requestCount

  const insertSuccessDeviceIds = res?.insertSuccessDevices?.map(x => x.deviceID) || []
  let existDeviceIds = res?.existDevices?.map(d => d.deviceID) || []
  let existDriverIds = res?.existDrivers || []
  let existDriversDeviceIds = res?.existDriversDevices?.map(d => d.deviceID) || []
  let insertFailedDeviceIds = res?.insertFailedDevices?.map(d => d.deviceID) || []
  let emptyCityDevices = res?.emptyCityIDDevices?.map(d => d.deviceID) || []
  let duplicateDeviceIdInReq = res?.duplicateDevicesInReq?.map(d => d.deviceID) || []
  let duplicateDriverDeviceIdsInReq = res?.duplicateDriverDevicesInReq?.map(d => d.deviceID) || []

  for (const deviceRecord of devices) {
    const deviceID = deviceRecord[KeyDeviceID]
    const driverID = deviceRecord[KeyDriverID]
    const isSuccess = insertSuccessDeviceIds.includes(deviceID)
    if (isSuccess) {
      result.accepted.push(deviceRecord)
    } else {
      let comment
      if (emptyCityDevices.includes(deviceID)) {
        comment = 'empty cityID device'
      }
      if (!comment && existDeviceIds.includes(deviceID)) {
        comment = 'exist device'
      }
      if (!comment && existDriverIds.includes(driverID)) {
        comment = 'exist driver'
      }
      if (!comment && duplicateDriverDeviceIdsInReq.includes(driverID)) {
        comment = 'duplicate driver devices in req'
      }
      if (!comment && duplicateDeviceIdInReq.includes(deviceID)) {
        comment = 'duplicate driver devices in req'
      }
      if (!comment && existDriversDeviceIds.includes(deviceID)) {
        comment = 'exist driver device'
      }
      if (!comment && insertFailedDeviceIds.includes(deviceID)) {
        comment = 'insert Failed Device'
      }
      comment = comment || 'unknown reason'
      result.rejected.push({ ...deviceRecord, comment })
    }
  }
  return result
}

type Props = {
  onSuccess: void
  onCancel: (e: React.MouseEvent<HTMLElement>) => void
}
type State = {
  isProcessing: boolean
  isUploadShow: boolean
  file: null | File
  submitResult: null | SubmitResult
}

export class BatchUpload extends React.Component<Props, State> {
  state = {
    isUploadShow: true,
    isProcessing: false,
    file: null,
    submitResult: null
  }

  async processFile (file): Promise<undefined | RecordItem[]> {
    if (file.type !== 'text/csv') {
      message.error('please select csv file')
      this.setState({ file: null })
      return
    }
    let csvJson
    try {
      csvJson = await csvToJSON(file, { skipEmptyLines: true })
    } catch (e) {
      message.error('parse csv failed ' + e.toString())
      return
    }
    let decodedContent: RecordItem[]
    try {
      decodedContent = await checkDataContent(csvJson)
    } catch (e) {
      message.error('csv  invalid:' + e.toString())
      return
    }
    return decodedContent
  }

  handleFileChange = async ({ file }) => {
    this.setState({ file })
    const recordItems = await this.processFile(file)
    if (recordItems) {
      await this.createDevices(recordItems)
    } else {
      this.setState({ file: null })
    }
  }

  async createDevices (devices: RecordItem[]) {
    const { file } = this.state
    const fileName = file.name
    this.setState({ isProcessing: true })
    try {
      const res = await addDevices(devices)
      const result = res.result
      const submitResult = await parseAddDeviceResult(result, devices)
      submitResult.fileName = fileName
      this.setState({ submitResult })
    } catch (error) {
      console.log(error, error.response)
      const result = error.response.data.result
      if (!result) {
        message.error(error.toString())
      }

      if (result.emptyIDCount > 0) {
        message.error('device ID is empty or city ID is empty')
      }
      const submitResult = await parseAddDeviceResult(result, devices)
      submitResult.fileName = fileName
      this.setState({ submitResult })
    } finally {
      this.setState({
        file: null,
        isUploadShow: false,
        isProcessing: false
      })
    }
  }

  handleUploadAnotherFile = () => {
    this.setState({
      submitResult: null,
      file: null,
      isUploadShow: true
    })
  }

  render () {
    const { isUploadShow, file, submitResult, isProcessing } = this.state
    const { onCancel } = this.props
    const props = {
      name: 'file',
      multiple: false,
      fileList: file ? [file] : [],
      beforeUpload: () => false,
      onChange: this.handleFileChange
    }
    let content
    if (isProcessing) {
      return (
        <div>
          <FakeProgress start={30} end={98} />
        </div>
      )
    }
    if (!!submitResult) {
      content = (
        <div>
          <SubmitResultContent submitResult={submitResult} />
          <Row justify='space-between' style={{ marginTop: 24 }}>
            <Col>
              <a
                style={{ color: '#1495E6', fontSize: '14px', lineHeight: '20px' }}
                onClick={this.handleUploadAnotherFile}
              >
                Upload another file
              </a>
            </Col>
            <Col>
              {/*<Button onClick={onCancel} style={{marginRight:8}}>Dismiss</Button>*/}
              <Button onClick={onCancel} type='primary'>
                Done
              </Button>
            </Col>
          </Row>
        </div>
      )
    } else if (isUploadShow) {
      content = (
        <div>
          <div style={{ height: '357px' }}>
            <UploadArea {...props}>
              <p className='ant-upload-drag-icon'>
                <InboxOutlined style={{ color: '#00A5CF', fontSize: 80 }} />
              </p>
              <p className='ant-upload-text'>
                Click or drag a .CSV file to this area to upload. Maximum 1 file allowed.
              </p>
            </UploadArea>
          </div>
          <Row justify='end' style={{ marginTop: 24 }}>
            <Col>
              <Button onClick={onCancel} style={{ marginRight: 8 }}>
                Dismiss
              </Button>
              <Button disabled type='primary'>
                Done
              </Button>
            </Col>
          </Row>
        </div>
      )
    }
    return <div>{content}</div>
  }
}

const SubmitResultContent = ({ submitResult }) => {
  const acceptCount = submitResult.accepted?.length || 0
  const rejectedCount = submitResult.total - acceptCount
  const onDownloadReport = () => {
    const csvContent = jsonToCSV<ProcessedRecordItem>(submitResult.rejected)
    const fileName = submitResult.fileName.toLowerCase().replace('.csv', '-failed.csv')
    downloadCSV(csvContent, fileName)
  }
  return (
    <div className='batch-create-result'>
      <Row className='header'>
        <Col>Results</Col>
      </Row>
      <Row className='row'>
        <Col span={12} className='label'>
          File name
        </Col>
        <Col span={12} className='value'>
          {submitResult.fileName}
        </Col>
      </Row>
      <Row className='row'>
        <Col span={12} className='label'>
          Number of fields
        </Col>
        <Col span={12} className='value'>
          {submitResult.total}
        </Col>
      </Row>
      <Row className='row'>
        <Col span={12} className='label'>
          Accepted
        </Col>
        <Col span={12} className='value'>
          {acceptCount}
        </Col>
      </Row>
      <Row className='row'>
        <Col span={12} className='label'>
          Rejected
        </Col>
        <Col span={12} className='value'>
          {rejectedCount}
          {!!rejectedCount && (
            <span>
              {' '}
              (<a onClick={onDownloadReport}>Download CSV with rejected fields</a>)
            </span>
          )}
        </Col>
      </Row>
    </div>
  )
}

const FakeProgress = ({ start, end }) => {
  let [percent, updatePercent] = useState(start)
  useEffect(() => {
    const updateProgress = () => {
      if (percent < end) {
        updatePercent(percent + 1)
        timer = setTimeout(updateProgress, Math.random() * 1000)
      }
    }
    let timer = setTimeout(updateProgress, Math.random() * 1000)
    return () => {
      clearTimeout(timer)
    }
  })
  return (
    <Row justify='center' align='middle' style={{ height: '357px', backgroundColor: '#F3F6FB' }}>
      <div style={{ textAlign: 'center' }}>
        <p className='ant-upload-drag-icon'>
          <InboxOutlined style={{ color: '#00A5CF', fontSize: 80 }} />
        </p>
        <Progress
          style={{ width: 330 }}
          strokeColor='#00A5CF'
          trailColor='#DBDBDB'
          percent={percent}
          showInfo={false}
        />
      </div>
    </Row>
  )
}
